import { toast } from '@/shared/toaster'
import * as Sentry from '@sentry/react'

const EXCLUDED = ['/auth']

const endpoint = (u: string) => {
  try {
    const { pathname } = new URL(u, location.origin)
    return pathname
  } catch {
    return u
  }
}

export const installFetchLogger = () => {
  if (typeof window === 'undefined' || !window.fetch) return
  const orig = window.fetch

  window.fetch = async (
    input: RequestInfo | URL,
    initial: RequestInit = {},
  ): Promise<Response> => {
    const url =
      typeof input === 'string' || input instanceof URL
        ? String(input)
        : input.url

    const { __disableGlobalErrorToastFilters = [], ...init } = initial

    const isExcluded = EXCLUDED.some((p) => url.includes(p))
    const startedAt = performance.now()

    const req =
      input instanceof Request
        ? new Request(input, { ...init })
        : new Request(url, { ...init })

    try {
      const res = await orig(req)
      const latency = Math.round(performance.now() - startedAt)
      const disabled = Array.isArray(__disableGlobalErrorToastFilters)
        ? __disableGlobalErrorToastFilters.some((fn) =>
            fn({ statusCode: res.status, statusText: res.statusText }),
          )
        : !!__disableGlobalErrorToastFilters
      const shouldToast =
        !isExcluded &&
        !disabled &&
        res.status >= 400 &&
        res.status !== 422 &&
        res.status !== 401

      const shouldLog = res.status >= 400 && res.status !== 422

      const endpointPath = endpoint(url)

      Sentry.addBreadcrumb({
        category: 'fetch',
        message: `${req.method} ${endpointPath}`,
        level: 'info',
        data: {
          method: req.method,
          endpoint: endpointPath,
          latency_ms: latency,
          headers: Object.fromEntries(req.headers.entries()),
        },
      })

      shouldLog &&
        res
          .clone()
          .json()
          .then(async (r) => {
            const err = r?.error ?? r
            const apiMessage =
              err?.message ||
              r?.message ||
              res.statusText ||
              'Something went wrong...'

            shouldToast && toast.error(apiMessage, { position: 'top-center' })

            let requestBody: unknown = undefined
            try {
              if (req.body && req.method !== 'GET') {
                const text = await req.text()
                requestBody = text ? JSON.parse(text) : text
              }
            } catch {
              /* ignore */
            }

            Sentry.captureMessage(`${endpointPath} | ${apiMessage}`, {
              level: res.status >= 500 ? 'error' : 'warning',
              fingerprint: ['{{ default }}', endpointPath, apiMessage],
              tags: {
                endpoint: endpoint(url),
                method: req.method || 'GET',
                http_status: String(res.status),
              },
              extra: {
                latency_ms: latency,
                request: {
                  url,
                  method: req.method,
                  headers: Object.fromEntries(req.headers.entries()),
                  body: requestBody,
                },
                response: {
                  status: res.status,
                  headers: Object.fromEntries(res.headers.entries()),
                  body: r,
                },
              },
            })
          })
          .catch(async (e) => {
            shouldToast &&
              toast.error(res.statusText || 'Something went wrong...', {
                position: 'top-center',
              })

            let requestBody: unknown = undefined
            try {
              if (req.body && req.method !== 'GET') {
                const text = await req.text()
                requestBody = text ? JSON.parse(text) : text
              }
            } catch {
              /* ignore */
            }

            Sentry.captureMessage(
              `${endpointPath} | ${res.statusText || 'Something went wrong...'}`,
              {
                level: res.status >= 500 ? 'error' : 'warning',
                tags: {
                  endpoint: endpoint(url),
                  method: req.method || 'GET',
                  http_status: String(res.status),
                },
                extra: {
                  latency_ms: latency,
                  request: {
                    url,
                    method: req.method,
                    headers: Object.fromEntries(req.headers.entries()),
                    body: requestBody,
                  },
                  response: {
                    status: res.status,
                    headers: Object.fromEntries(res.headers.entries()),
                    body: e,
                  },
                },
              },
            )
          })

      return res
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      const code =
        e?.name === 'AbortError'
          ? 'ABORT'
          : !navigator.onLine
            ? 'NETWORK'
            : 'NETWORK'

      const disabled = Array.isArray(__disableGlobalErrorToastFilters)
        ? __disableGlobalErrorToastFilters.some((fn) =>
            fn({ offline: code === 'NETWORK' }),
          )
        : !!__disableGlobalErrorToastFilters
      const shouldToast = !isExcluded && !disabled && code === 'NETWORK'

      const shouldLog = code === 'NETWORK'

      Sentry.addBreadcrumb({
        category: 'fetch',
        level: 'error',
        message: `${req.method} ${endpoint(url)} failed`,
        data: {
          endpoint: endpoint(url),
          method: req.method,
          error: e?.message,
        },
      })

      const endpointPath = endpoint(url)

      shouldLog &&
        Sentry.captureMessage(`${endpointPath} | API network error`, {
          fingerprint: ['{{ default }}', endpointPath, 'API network error'],
          level: 'error',
          tags: {
            endpoint: endpoint(url),
            method: req.method || 'GET',
          },
          extra: {
            message: e?.message,
            stack: e?.stack,
          },
        })

      shouldToast &&
        toast.error('Network error', {
          position: 'top-center',
        })

      throw e
    }
  }
}
